import { useEffect, useCallback, useRef } from 'react';

type EventType = 'mousedown' | 'mouseup' | 'touchstart' | 'touchend';

/**
 * Custom hook that detects clicks or touches outside a referenced element.
 * T - The type of the HTML element to attach the ref to.
 * @param callback - Function to be called when a click/touch occurs outside the element.
 * @param eventType - Type of event to listen for ('mousedown', 'mouseup', 'touchstart', 'touchend'). Defaults to 'mousedown'.
 * @returns A ref object to attach to the element to monitor for outside clicks.
 */
export function useOutsideClick<T extends HTMLElement = HTMLElement>(
  callback: (event: MouseEvent | TouchEvent) => void,
  eventType: EventType = 'mousedown',
) {
  const ref = useRef<T>(null);

  const handleClick = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        callback(e);
      }
    },
    [callback, ref],
  );

  useEffect(() => {
    document.addEventListener(eventType, handleClick);

    return () => {
      document.removeEventListener(eventType, handleClick);
    };
  }, [eventType, handleClick]);

  return ref;
}
